import { Link, useNavigate } from '@tanstack/react-router';
import { ChevronDown, CircleHelp, LogOut, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Avatar, AvatarFallback } from '@nucleus-commerce/ui/components/avatar';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@nucleus-commerce/ui/components/dropdown-menu';

import { session } from '@/shared/session/session';

function initials(name: string) {
  return name
    .split(' ')
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

/** Top-bar account button: who is signed in, their platform role, and sign out. */
export function UserMenu() {
  const { t } = useTranslation(['auth', 'common']);
  const { me } = session.useSession();
  const logout = session.useLogout();
  const navigate = useNavigate();

  const signOut = () => {
    logout.mutate(undefined, { onSettled: () => void navigate({ to: '/', replace: true }) });
  };

  if (!me) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 gap-2 pr-1.5 pl-1"
          aria-label={me.user.name}
        >
          <Avatar className="size-6">
            <AvatarFallback className="bg-muted text-[11px] font-semibold">
              {initials(me.user.name)}
            </AvatarFallback>
          </Avatar>
          <span className="hidden max-w-40 truncate text-sm font-medium sm:inline">
            {me.user.name}
          </span>
          {me.twoFactorEnabled ? (
            <ShieldCheck
              className="text-primary hidden size-3.5 sm:inline"
              role="img"
              aria-label={t('auth:userMenu.twoFactorOn')}
            />
          ) : null}
          <ChevronDown className="text-muted-foreground size-3.5" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuLabel className="flex flex-col gap-0.5 font-normal">
          <span className="truncate text-sm font-medium">{me.user.name}</span>
          <span className="text-muted-foreground truncate text-xs">{me.user.email}</span>
          <span className="text-primary pt-1 text-xs font-medium">
            {t('auth:userMenu.role', { role: t(`common:roles.${me.user.role}`) })}
          </span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {me.twoFactorRequired ? null : (
          <DropdownMenuItem asChild>
            <Link to="/help">
              <CircleHelp aria-hidden />
              {t('auth:userMenu.help')}
            </Link>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem asChild>
          <Link to="/account/security">
            <ShieldCheck aria-hidden />
            {t('auth:userMenu.security')}
            {me.twoFactorEnabled ? (
              <Badge variant="secondary" className="ml-auto" data-testid="two-factor-badge">
                {t('auth:userMenu.twoFactorBadge')}
              </Badge>
            ) : null}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={signOut} disabled={logout.isPending}>
          <LogOut aria-hidden />
          {t('auth:userMenu.signOut')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
